import React, { useEffect } from 'react'
import { SiteHeader } from '../components/site-header'
import { METADATA } from '../components/meta/metadata'

export default function TermsPage() {
  useEffect(() => {
    document.title = METADATA.terms.title

    let metaDescription = document.querySelector('meta[name="description"]')
    if (!metaDescription) {
      metaDescription = document.createElement('meta')
      metaDescription.setAttribute('name', 'description')
      document.head.appendChild(metaDescription)
    }
    metaDescription.setAttribute('content', METADATA.terms.description)
  }, [])

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <div className="container pt-28 pb-16 space-y-8">
          <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl">Terms of Service</h1>
          <div className="prose prose-gray dark:prose-invert max-w-none space-y-6">
            <p>Last updated: May 2026</p>

            <p>These terms govern your use of the Syllepsis website and our survey and diagnostic platform (together, the 'Services'). By accessing or using the Services, you accept these terms. If you do not accept them, please do not use the Services. Professional services such as consultancy and AI engagements are governed by a separate written agreement between you and Syllepsis, which prevails over these terms for that work.</p>

            <h2 className="text-2xl font-semibold">1. Who We Are</h2>
            <p>The Services are provided by Syllepsis Ltd ('Syllepsis', 'we', 'us' or 'our'), an AI innovation and knowledge engineering firm registered in England and Wales. You can contact us at info@syllepsis.io.</p>

            <h2 className="text-2xl font-semibold">2. Accounts</h2>
            <p>When you create an account on our platform:</p>
            <ul className="list-disc pl-6">
              <li>You must provide accurate and complete information</li>
              <li>You are responsible for keeping your account credentials confidential</li>
              <li>You must notify us immediately of any unauthorised use of your account</li>
              <li>Each account is limited in the number of surveys it can create and process</li>
              <li>We may suspend or terminate accounts that breach these terms</li>
            </ul>

            <h2 className="text-2xl font-semibold">3. Survey and Diagnostic Services</h2>
            <p>Our survey and diagnostic services are subject to the following conditions:</p>
            <ul className="list-disc pl-6">
              <li>Survey responses are collected anonymously, with only organisational role information recorded</li>
              <li>Survey owners are responsible for obtaining any permissions needed from their organisation and respondents</li>
              <li>We may limit the number of responses per survey</li>
              <li>Anonymous survey data may be processed using AI to generate insights</li>
              <li>Survey access codes must not be shared beyond their intended recipients</li>
              <li>Insights are provided for information and do not constitute professional advice</li>
            </ul>

            <h2 className="text-2xl font-semibold">4. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6">
              <li>use the Services for any unlawful or fraudulent purpose</li>
              <li>attempt to gain unauthorised access to the Services or related systems</li>
              <li>disrupt or interfere with the Services or their security</li>
              <li>copy, scrape or republish content except as expressly permitted</li>
              <li>upload content that is unlawful, infringing or harmful</li>
            </ul>

            <h2 className="text-2xl font-semibold">5. Professional Engagements</h2>
            <p>Information on this website about our services is for general information and does not constitute an offer or professional advice. Any consultancy, knowledge engineering or AI engagement is subject to a separate written agreement, including its own scope, fees and terms. Nothing on this website forms a binding contract for professional services.</p>

            <h2 className="text-2xl font-semibold">6. Intellectual Property</h2>
            <p>All content on this website and platform, including text, graphics, logos, diagnostic frameworks and survey methodologies, is owned by Syllepsis Ltd or its licensors and is protected by intellectual property laws. We grant you a limited, non-exclusive and non-transferable licence to use the Services for their intended purpose. Where we build a Knowledge Architecture or other deliverables for a client, ownership of those deliverables is set out in the relevant engagement agreement, and the client retains ownership of its own institutional knowledge.</p>

            <h2 className="text-2xl font-semibold">7. Data Protection</h2>
            <p>We handle personal data in accordance with our Privacy Policy. By using the Services, you acknowledge that we may store and process anonymous survey responses and use cookies as described in that policy.</p>

            <h2 className="text-2xl font-semibold">8. Disclaimers</h2>
            <p>The Services are provided on an 'as is' and 'as available' basis. To the extent permitted by law, we exclude all warranties, whether express or implied, including any implied warranties of satisfactory quality, fitness for a particular purpose and non-infringement. We do not warrant that the Services will be uninterrupted, error-free or secure, or that any insights generated will be accurate or complete.</p>

            <h2 className="text-2xl font-semibold">9. Limitation of Liability</h2>
            <p>Nothing in these terms excludes or limits our liability for death or personal injury caused by our negligence, for fraud or fraudulent misrepresentation, or for any liability that cannot be excluded under applicable law.</p>
            <p>Subject to that, we are not liable for any indirect or consequential loss, or for loss of profit, revenue, data or goodwill, arising from your use of or inability to use the Services. Our total liability arising out of or in connection with the Services is limited to the greater of the fees you paid us for the relevant Service in the 12 months before the claim, or £100.</p>

            <h2 className="text-2xl font-semibold">10. Indemnity</h2>
            <p>You agree to indemnify us against any claims, losses and costs arising from your misuse of the Services or your breach of these terms.</p>

            <h2 className="text-2xl font-semibold">11. Suspension and Changes to the Services</h2>
            <p>We may modify, suspend or withdraw all or part of the Services, and may refuse or terminate access, where this is reasonable or required. You may stop using the Services at any time.</p>

            <h2 className="text-2xl font-semibold">12. Changes to These Terms</h2>
            <p>We may update these terms from time to time. The date at the top shows when they were last revised, and continued use of the Services after a change means you accept the updated terms.</p>

            <h2 className="text-2xl font-semibold">13. Governing Law</h2>
            <p>These terms are governed by the laws of England and Wales. The courts of England and Wales have exclusive jurisdiction over any dispute, although we may bring proceedings in your country of residence where required by law.</p>

            <h2 className="text-2xl font-semibold">14. Severability and Entire Agreement</h2>
            <p>If any provision of these terms is found to be unenforceable, the remaining provisions continue in full force. These terms, together with our Privacy Policy and any separate engagement agreement, form the entire agreement between you and Syllepsis regarding the Services.</p>

            <h2 className="text-2xl font-semibold">15. Contact</h2>
            <p>For any question about these terms, contact us at info@syllepsis.io.</p>
          </div>
        </div>
      </main>
    </div>
  )
}
